import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';

import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MatSortModule } from '@angular/material/sort';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatLegacyTooltipDefaultOptions as MatTooltipDefaultOptions, MatLegacyTooltipModule as MatTooltipModule, MAT_LEGACY_TOOLTIP_DEFAULT_OPTIONS as MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/legacy-tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';

import { ClipboardModule } from '@angular/cdk/clipboard';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SwiperModule } from 'ngx-swiper-wrapper-v-13';
import { SWIPER_CONFIG } from 'ngx-swiper-wrapper-v-13';
import { SwiperConfigInterface } from 'ngx-swiper-wrapper-v-13';

import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';

import { NgxUploaderModule } from 'ngx-uploader-esb';
import { OwlDateTimeModule, OwlNativeDateTimeModule, OWL_DATE_TIME_FORMATS } from '@danielmoncada/angular-datetime-picker';
import { NgSelectModule } from '@ng-select/ng-select';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { NgxMaskModule } from 'ngx-mask';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { AppRoutes } from './app.routing';
import { AppComponent } from './app.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { PlaylistComponent } from './components/playlists/playlist/playlist.component';
import { PlaylistLocationFilter } from './core/pipes/playlist-location-filter.pipe';
import { OrderByPipe } from './core/pipes/order-by.pipe';
import { PlaylistItemComponent } from './components/playlists/playlist-item/playlist-item.component';
import { AdminComponent } from './pages/admin/admin.component';
import { AuthAndErrorInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from './core/services/auth/auth.service';
import { AuthGuard } from './core/guards/auth.guard';
import { ConfigModule, AppConfig } from './app.settings';
import { PlaylistItemDialogService } from './core/services/dialog-services/playlist-item-dialog.service';
import { ScheduleSettingsDialogService } from './core/services/dialog-services/schedule-dialog.service';
import { RoleGuard } from './core/guards/role.guard';
import { EditPlaylistComponent } from './components/playlists/form-edit-playlist/edit-playlist.component';
import { ScreenAssignComponent } from './components/item-screen-assign/item-screen-assign.component';
import { ScreenScheduleComponent } from './components/item-screen-schedule/item-screen-schedule.component';
import { ListItemComponent } from './components/item-view-list/list-item.component';
import { MonitorComponent } from './components/item-view-monitor/monitor.component';
import { BaseViewItemComponent } from './components/_basic/item-view-base.component';
import { MonitorMenuService } from './core/services/dialog-services/monitor-menu.service';
import { ScreensStateService } from './core/services/screen-state/screens-state.service';
import { UIService } from './core/services/utils/ui.service';
import { PlaylistItemDialogComponent } from './components/modal-playlist-item/playlist-item-dialog.component';
import { MonitorsFilterComponent } from './components/area-filters-panel/monitors-filter.component';
import { BottomMenuComponent } from './components/area-bottom-menu/bottom-menu.component';
import { LibraryComponent } from './components/area-library/library.component';
import { MainContentComponent } from './components/area-main-content/main-content.component';
import { ScheduleSettingsComponent } from './components/schedule-settings/schedule-settings.component';
import { TimelineComponent } from './components/timeline/timeline.component';
import { MonitorMenuComponent } from './components/monitor-menu/monitor-menu.component';
import { GreyoutComponent } from './misc/greyout.component';
import { NotFoundErrorPageComponent } from './misc/error-pages/not-found.component';
import { ForbiddenErrorPageComponent } from './misc/error-pages/forbidden.component';
import { PlayboardComponent } from './components/area-playboard/playboard.component';
import { TimelineMonitorMenuComponent } from './components/monitor-menu/timeline-monitor-menu.component';
import { MultiselectModule } from './misc/multiselect/multiselect.module';
import { UrlService } from './core/services/utils/url.service';
import { RecentFileComponent } from './components/tab-files/tab-recent-files/recent-file/recent-file.component';
import { TabUrlComponent } from './components/tab-files/tab-url/tab-url.component';
import { TabFileComponent } from './components/tab-files/tab-file/tab-file.component';
import { TabRecentFilesComponent } from './components/tab-files/tab-recent-files/tab-recent-files.component';
import { TabLocalAppComponent } from './components/tab-files/tab-local-files/tab-local-file.component';
import { ItemTabComponent } from './components/_basic/tab-playlist-item.component';
import { DurationPickerComponent } from './misc/duration-picker/duration-picker.component';
import { DashboardOutletComponent } from './pages/dashboard-outlet/dashboard-outlet.component';
import { RouterModule } from '@angular/router';
import { LoginComponent } from './pages/login/login.component';
import { SharedModule } from './shared-module/shared.module';
import { HeaderModule } from './misc/header/header.module';
import { PlaylistsService } from './core/services/http-services/playlists.service';
import { PlaylistItemsService } from './core/services/http-services/playlistItems.service';
import { SchedulesService } from './core/services/http-services/schedules.service';
import { MonitorsService } from './core/services/http-services/monitors.service';
import { FilesService } from './core/services/http-services/files.service';
import { DeleteDialogService } from './core/services/dialog-services/delete-dialog.service';
import { MachinesService } from './core/services/http-services/machines.service';
import { AdministrationService } from './core/services/http-services/administrator.service';
import { UtilsService } from './core/services/http-services/utils.service';
import { LaunchersService } from './core/services/http-services/launchers.service';
import { PlayerService } from './core/services/http-services/player.service';
import { MonitorHub } from './shared-module/hubs/monitor.hub';
import { ModalWidowUsersComponent } from './admin-module/modal-edit-user/modal-edit-user.component';
import { EditUserDialogService } from './admin-module/admin-services/edit-user-dialog.service';
import { AdministratorPlaylistService } from './core/services/http-services/administrator.playlist.service';
import { ScheduleTimeComponent } from './shared-module/components/_modals/schedule-time/schedule-time.component';
import { LoaderModule } from './core/elements/loader/loader.module';
import { MonitorFilterPipe } from './core/pipes/monitor-filter.pipe';
import { LoginGuard } from './core/guards/login.guard';
import { TagsComponent } from './components/tags/tags.component';
import { EvaluationPeriodExpiredComponent } from './misc/evaluation-period-expired/evaluation-period-expired.component';
import { ModalRequestLicenseComponent } from './admin-module/modal-request-license/modal-request-license.component';
import { ModalInstallLicenseComponent } from './admin-module/modal-install-license/modal-install-license.component';
import { TagItemComponent } from './components/tags/tag-item/tag-item.component';
import { MainLibraryFormComponent } from './components/main-library-form/main-library-form.component';
import { TagFormComponent } from './components/tags/tag-form/tag-form.component';
import { ElementsModule } from './core/elements/elements.module';
import { MonitorTagFilterPipe } from './core/pipes/monitor-tag-filter.pipe';
import { LicenseGuard } from './core/guards/license.guard';
import { AdminTabsGuard } from './core/guards/admin-tabs.guard';
import { ServerDataBackupService } from './core/services/http-services/server-data-backup.service';
import { SidebarToggleDirective } from './_directives/sidebar-toggle.directive';
import { AdministratorMonitorTagService } from './core/services/http-services/administrator.monitor-tag.service';
import { TransformMonitorKeyToTitlePipe } from './core/pipes/transform-monitor-key-to-title.pipe';
import { PdfAssignerComponent } from './pages/pdf-assigner/pdf-assigner.component';
import { ErrorPagesComponent } from './misc/error-pages/error-pages.component';
import { ItemViewFullScreenComponent } from './components/item-view-full-screen/item-view-full-screen.component';
import { LauncherFeaturesComponent } from './components/tab-files/launcher-features/launcher-features.component';
import { EvaluationPeriodNotificationComponent } from './misc/evaluation-period-notification/evaluation-period-notification.component';
import { FullScreenComponent } from './components/full-screen/full-screen.component';
import { ModalFullScreenLinkGeneratorComponent } from './shared-module/components/_modals/modal-full-screen-view-link/modal-full-screen-link-generator.component';
import { AlertService } from './wall-module/shared/services/alert.service';
import { SafeHtmlPipe } from './wall-module/shared/pipes/safe-html.pipe';
import { AlertComponent } from './wall-module/shared/components/popups/alert/alert.component';
import { MonitorRemoteControlComponent } from './components/monitor-remote-control/monitor-remote-control.component';
import { FileComponent } from './components/tab-files/tab-file/file/file.component';
import { NumberToArrayPipe } from './core/pipes/number-to-array.pipe';
import { CategoryFilterPipe } from './core/pipes/category-filter.pipe';
import { FindFileByIdPipe } from './core/pipes/find-file-by-id.pipe';
import { DateToLocalePipe } from './core/pipes/date-to-locale.pipe';
import { MclModeSelectComponent } from './components/mcl-mode-select/mcl-mode-select.component';
import { ModalAssignAndroidDeviceComponent } from './shared-module/components/_modals/modal-assign-android-device/modal-assign-android-device.component';
import { AllMonitorsCheckedPipe } from './core/pipes/all-monitors-checked.pipe';
import { MonitorSortPipe } from './core/pipes/monitor-sort.pipe';
import { ModalTwoFactorAuthenticationComponent } from './shared-module/components/_modals/modal-two-factor-authentication/modal-two-factor-authentication.component';
import { ModalConfirmChangePlaylistTypeComponent } from './components/playlists/modal-change-playlist-type-confirm/modal-confirm-change-playlist-type/modal-confirm-change-playlist-type.component';
import { ModalConfigureRssComponent } from './shared-module/components/_modals/modal-configure-rss/modal-configure-rss.component';
import { RssSourceComponent } from './components/rss-source/rss-source.component';
import { ModalCreateMachineCategoryComponent } from './shared-module/components/_modals/modal-create-agent-category/modal-create-machine-category.component';
import { ModalEditLocationComponent } from './shared-module/components/_modals/modal-edit-location/modal-edit-location.component';
import { ArrayContainsElementPipe } from './core/pipes/array-contains-element.pipe';
import { IsMawChromeLauncherPipe } from './core/pipes/is-maw-chrome-launcher.pipe';
import { ModalAddUserApiTokenComponent } from './shared-module/components/_modals/modal-add-user-api-token/modal-add-user-api-token.component';
import { ZoomInputComponent } from './components/zoom-input/zoom-input.component';
import { LocationFilterPipe } from './core/pipes/location-filter.pipe';
import { ModalConfigureItemBorderComponent } from './shared-module/components/_modals/modal-configure-item-border/modal-configure-item-border.component';
import { ModalLegalConsentComponent } from './shared-module/components/_modals/modal-legal-consent/modal-legal-consent.component';
import { PlaylistItemAdvancedOptionsComponent } from './components/playlist-item-advanced-options/playlist-item-advanced-options.component';
import { PaginationComponent } from './core/elements/pagination/pagination.component';
import { PaginatorFilterPipe } from './core/pipes/paginator-filter.pipe';
import { IsAnyPlaylistItemProcessingPipe } from './core/pipes/is-any-playlist-item-processing.pipe';
import { ItemContentPreviewComponent } from './components/tab-files/item-content-preview/item-content-preview.component';
import { FilesProgressBarComponent } from './components/tab-files/files-progress-bar/files-progress-bar.component';
import { TimeInputComponent } from './misc/time-input/time-input.component';
import { ArrayIncludesPipe } from './core/pipes/array-includes.pipe';
import { FindAssignedMonitorsPipe } from './core/pipes/find-assigned-monitors.pipe';
import { ImageSrcDirective } from './core/directives/image-src.directive';
import { CalcucateRssPositionPipe } from './core/pipes/calcucate-rss-position.pipe';
import { TabForegroundItemComponent } from './components/tab-files/tab-foreground-item/tab-foreground-item.component';

export const MY_NATIVE_FORMATS = {
  fullPickerInput: { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
  datePickerInput: { year: 'numeric', month: 'numeric', day: 'numeric' },
  timePickerInput: { hour: 'numeric', minute: 'numeric', second: 'numeric' },
  monthYearLabel: { year: 'numeric', month: 'short' },
  dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
  monthYearA11yLabel: { year: 'numeric', month: 'long' },
};

const DEFAULT_SWIPER_CONFIG: SwiperConfigInterface = {
  direction: 'horizontal',
  slidesPerView: 'auto'
};

const MAT_TOOLTIP_CUSTOM_OPTIONS: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 0,
  disableTooltipInteractivity: true,
}

@NgModule({
    declarations: [
        AppComponent,
        MonitorComponent,
        PlaylistComponent,
        DashboardComponent,
        PlaylistItemDialogComponent,
        MonitorsFilterComponent,
        TabUrlComponent,
        TabFileComponent,
        BottomMenuComponent,
        ModalWidowUsersComponent,
        ModalRequestLicenseComponent,
        ModalInstallLicenseComponent,
        LibraryComponent,
        PlaylistLocationFilter,
        MainContentComponent,
        OrderByPipe,
        EditPlaylistComponent,
        PlaylistItemComponent,
        ScheduleSettingsComponent,
        TabRecentFilesComponent,
        TimelineComponent,
        MonitorMenuComponent,
        ListItemComponent,
        ScheduleTimeComponent,
        GreyoutComponent,
        AdminComponent,
        ProfileComponent,
        NotFoundErrorPageComponent,
        ErrorPagesComponent,
        ForbiddenErrorPageComponent,
        TabLocalAppComponent,
        PlayboardComponent,
        ScreenAssignComponent,
        ScreenScheduleComponent,
        TimelineMonitorMenuComponent,
        RecentFileComponent,
        BaseViewItemComponent,
        ItemTabComponent,
        DashboardOutletComponent,
        LoginComponent,
        MonitorFilterPipe,
        EvaluationPeriodExpiredComponent,
        TagsComponent,
        TagItemComponent,
        MainLibraryFormComponent,
        TagFormComponent,
        MonitorTagFilterPipe,
        SidebarToggleDirective,
        TransformMonitorKeyToTitlePipe,
        ItemViewFullScreenComponent,
        LauncherFeaturesComponent,
        PdfAssignerComponent,
        EvaluationPeriodNotificationComponent,
        FullScreenComponent,
        ModalFullScreenLinkGeneratorComponent,
        SafeHtmlPipe,
        MonitorRemoteControlComponent,
        AlertComponent,
        FileComponent,
        NumberToArrayPipe,
        CategoryFilterPipe,
        FindFileByIdPipe,
        DateToLocalePipe,
        MclModeSelectComponent,
        ModalAssignAndroidDeviceComponent,
        AllMonitorsCheckedPipe,
        MonitorSortPipe,
        ModalTwoFactorAuthenticationComponent,
        ModalConfirmChangePlaylistTypeComponent,
        ModalConfigureRssComponent,
        ModalCreateMachineCategoryComponent,
        ModalEditLocationComponent,
        ArrayContainsElementPipe,
        IsMawChromeLauncherPipe,
        ModalAddUserApiTokenComponent,
        LocationFilterPipe
    ],
    bootstrap: [AppComponent],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ],
    imports: [
        FormsModule,
        SwiperModule,
        RouterModule.forRoot(AppRoutes, {
            useHash: true
        }),
        BrowserModule,
        BrowserAnimationsModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatDialogModule,
        MatCardModule,
        MatIconModule,
        MatMenuModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatRippleModule,
        MatSidenavModule,
        MatSliderModule,
        MatSlideToggleModule,
        MatTabsModule,
        MatToolbarModule,
        MatTooltipModule,
        MatRadioModule,
        MatFormFieldModule,
        DragDropModule,
        ReactiveFormsModule,
        NgxUploaderModule,
        NgSelectModule,
        ToastrModule.forRoot({
            timeOut: 10000,
            closeButton: true
        }),
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        MultiselectModule,
        MatListModule,
        MatDividerModule,
        MatTableModule,
        MatSortModule,
        MatInputModule,
        MatSelectModule,
        MatExpansionModule,
        MatSnackBarModule,
        SharedModule,
        HeaderModule,
        TimepickerModule.forRoot(),
        NgxMaskModule.forRoot(),
        LoaderModule,
        ElementsModule,
        ClipboardModule,
        ZoomInputComponent,
        ModalConfigureItemBorderComponent,
        ModalLegalConsentComponent,
        PlaylistItemAdvancedOptionsComponent,
        NgbModule,
        ModalConfigureItemBorderComponent,
        PaginationComponent,
        PaginatorFilterPipe,
        IsAnyPlaylistItemProcessingPipe,
        ItemContentPreviewComponent,
        FilesProgressBarComponent,
        TimeInputComponent,
        ArrayIncludesPipe,
        FindAssignedMonitorsPipe,
        ImageSrcDirective,
        CalcucateRssPositionPipe,
        TabForegroundItemComponent,
        RssSourceComponent,
        DurationPickerComponent
    ],
    providers: [
        {
            provide: OWL_DATE_TIME_FORMATS,
            useValue: MY_NATIVE_FORMATS
        },
        {
            provide: SWIPER_CONFIG,
            useValue: DEFAULT_SWIPER_CONFIG
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthAndErrorInterceptor,
            multi: true
        },
        {
            provide: 'Window',
            useValue: window
        },
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy
        },
        {
            provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
            useValue: MAT_TOOLTIP_CUSTOM_OPTIONS
        },
        UrlService,
        AuthService,
        MonitorHub,
        PlaylistsService,
        PlaylistItemsService,
        SchedulesService,
        ScheduleSettingsDialogService,
        MonitorsService,
        FilesService,
        HttpClient,
        AppConfig,
        PlaylistItemDialogService,
        DeleteDialogService,
        MachinesService,
        ConfigModule.init(),
        AuthGuard,
        AdminTabsGuard,
        RoleGuard,
        UIService,
        AdministrationService,
        UtilsService,
        ServerDataBackupService,
        MonitorMenuService,
        ScreensStateService,
        LaunchersService,
        PlayerService,
        EditUserDialogService,
        AdministratorPlaylistService,
        AdministratorMonitorTagService,
        LoginGuard,
        LicenseGuard,
        MonitorFilterPipe,
        MonitorTagFilterPipe,
        ServerDataBackupService,
        AlertService,
        provideHttpClient(withInterceptorsFromDi()),
    ]
})

export class AppModule { }
